/// ///////////////////////////////////////////////////////////////////////////////
// Copyright (C) 2026 CONQUER INNOVATIONS SP. Z O.O. Global B.V. and its affiliate(s).
// All rights reserved.
//
// This software and other materials contain proprietary information
// controlled by CONQUER INNOVATIONS SP. Z O.O. and are protected by applicable copyright legislation.
// Any use and utilization of this software and other materials and
// disclosure to any third parties is conditional upon having a separate
// agreement with CONQUER INNOVATIONS SP. Z O.O. for the access, use, utilization or disclosure of this
// software. In the absence of such agreement, the use of the software is not
// allowed.
// The creator, author and originator of the code in the current file: Michał Pawłowski
//////////////////////////////////////////////////////////////////////////////////
<template>
  <div class="common-import-form card shadow-sm">
    <div class="card-header bg-light">
      <h5 class="mb-0 text-primary"><i class="fas fa-file-import me-2"></i> {{ title }}</h5>
    </div>
    <div class="card-body">
      <div v-if="description" class="alert alert-info">
        <i class="fas fa-info-circle me-2"></i>
        {{ description }}
      </div>

      <div v-if="step === 1">
        <div class="mb-3">
          <label class="form-label fw-bold">1. Wybierz plik źródłowy</label>
          <input
              type="file"
              class="form-control"
              @change="onFileChange"
              accept=".xlsx, .xls, .csv"
          />
          <div class="form-text">Obsługiwane formaty: .xlsx, .xls, .csv</div>
        </div>
      </div>

      <div v-if="step === 2">
        <div class="mb-3">
          <label class="form-label fw-bold">2. Wybierz arkusz</label>
          <select v-model="selectedSheetName" class="form-select" @change="loadSheet">
            <option v-for="sheet in sheetNames" :key="sheet" :value="sheet">{{ sheet }}</option>
          </select>
        </div>
      </div>

      <div v-if="step === 3">

        <div class="row mb-4">
          <div class="col-12">
            <div class="bg-light border rounded p-3">
              <label class="form-label fw-bold mb-2">
                <i class="fas fa-scissors me-1"></i> Dostosowanie początku pliku
              </label>
              <div class="d-flex align-items-center gap-3">
                <div style="width: 150px;">
                  <label class="form-label small text-muted mb-1">Pomiń wiersze od góry:</label>
                  <input
                      type="number"
                      min="0"
                      class="form-control"
                      v-model.number="rowsToSkip"
                  >
                </div>
                <div class="form-text small text-muted mt-3">
                  Zmień tę wartość, jeśli plik zawiera metadane lub puste wiersze przed właściwym nagłówkiem tabeli.
                  Podgląd i lista kolumn zaktualizują się automatycznie.
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="row mb-4">
          <div class="col-12">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h6 class="mb-0 fw-bold">Podgląd danych (pierwsze {{ previewRows }} wierszy danych)</h6>
              <small class="text-muted">Wiersz nagłówkowy jest używany do mapowania</small>
            </div>

            <div v-if="fileHeaders.length === 0" class="alert alert-warning mb-0">
              <i class="fas fa-exclamation-triangle me-2"></i>
              Nie wykryto wiersza nagłówkowego. Sprawdź ilość pomijanych wierszy lub plik.
            </div>

            <div v-else class="table-responsive border rounded bg-white p-2" style="max-height: 300px; font-size: 0.85rem;">
              <table class="table table-sm table-striped table-hover mb-0">
                <thead class="table-light sticky-top">
                <tr>
                  <th v-for="(header, idx) in fileHeaders" :key="'hdr'+idx" class="text-nowrap">
                    {{ header || `[Kolumna ${idx+1}]` }}
                  </th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(row, rowIdx) in previewData" :key="'row'+rowIdx">
                  <td v-for="(header, colIdx) in fileHeaders" :key="'col'+colIdx" class="text-nowrap">
                    {{ row[header] !== undefined ? row[header] : '' }}
                  </td>
                </tr>
                <tr v-if="previewData.length === 0">
                  <td :colspan="fileHeaders.length" class="text-center text-muted py-3">
                    Brak danych do wyświetlenia po nagłówku.
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-12">
            <h6 class="border-bottom pb-2 fw-bold mb-3">3. Mapowanie Kolumn</h6>

            <div v-if="fileHeaders.length > 0">
              <div v-if="showIdentifierValidationError" class="alert alert-danger">
                <i class="fas fa-exclamation-circle me-2"></i>
                Wybierz przynajmniej jedno pole identyfikacyjne.
                <div v-if="identifierFieldLabels.length" class="small mt-2">
                  Dostępne pola identyfikacyjne:
                  <span class="fw-semibold">{{ identifierFieldLabels.join(', ') }}</span>
                </div>
              </div>
              <div class="table-responsive" style="max-height: 400px; overflow-y: auto;">
                <table class="table table-sm table-bordered table-hover align-middle">
                  <thead class="table-light">
                  <tr>
                    <th style="width: 40%">Pole docelowe w systemie</th>
                    <th style="width: 10%" class="text-center">Wymagane</th>
                    <th style="width: 50%">Kolumna w pliku źródłowym</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="field in fieldDefinitions" :key="field.key">
                    <td>
                      <strong>{{ field.label }}</strong>
                      <div class="small text-muted fst-italic" v-if="field.description">{{ field.description }}</div>
                    </td>
                    <td class="text-center">
                      <span v-if="field.required" class="badge bg-danger">Tak</span>
                      <span v-else class="badge bg-secondary opacity-50">Nie</span>
                    </td>
                    <td>
                      <select
                          class="form-select"
                          v-model="mapping[field.key]"
                          :class="{'is-invalid': field.required && !mapping[field.key], 'border-primary': mapping[field.key]}"
                      >
                        <option :value="null">-- Ignoruj to pole --</option>
                        <option v-for="(header, idx) in fileHeaders" :key="'opt'+idx" :value="header">
                          {{ header || `[Kolumna ${idx+1}]` }}
                        </option>
                      </select>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div v-else class="alert alert-info">
              Zdefiniuj poprawny wiersz nagłówkowy powyżej, aby rozpocząć mapowanie.
            </div>
          </div>
        </div>

      </div>

      <div class="d-flex justify-content-end align-items-center gap-2 mt-4 pt-3 border-top">
        <button v-if="step > 1" class="btn btn-outline-secondary px-3" @click="reset">
          <i class="fas fa-undo-alt me-1"></i> Zacznij od nowa
        </button>

        <button
            v-if="step === 3"
            class="btn btn-primary px-4"
            @click="processImport"
            :disabled="!isMappingValid || fileHeaders.length === 0"
        >
          <i class="fas fa-cloud-upload-alt me-1"></i> Importuj dane
        </button>
      </div>

    </div>
  </div>
</template>

<script>
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import Swal from "sweetalert2";
import { useCommonImportModule } from "@/state/pinia/commonImportModule";

export default {
  name: "CommonTableImportForm",
  props: {
    title: {
      type: String,
      default: "Import danych"
    },
    description: {
      type: String,
      default: ""
    },
    // Definicja pól docelowych: [{ key: 'name', label: 'Nazwa', required: true, type: 'string' }]
    fieldDefinitions: {
      type: Array,
      required: true
    },
    // Typ importu dla endpointu backendu (np. 'package-orders')
    importType: {
      type: String,
      required: false // Wymagane jeśli mode='submit'
    },
    mode: {
      type: String,
      default: 'submit', // 'emit' lub 'submit'
      validator: (v) => ['emit', 'submit'].includes(v)
    },
    previewRows: {
      type: Number,
      default: 5
    },
    identifierKeys: {
      type: Array,
      default: () => []
    },
    meta:{
      type: Object,
      default: () => ({})
    }
  },
  emits: ['onSave', 'onSaved', 'onError'],
  setup() {
    const commonImportModule = useCommonImportModule();
    return { commonImportModule };
  },
  data() {
    return {
      step: 1,
      file: null,
      workbook: null,
      sheetNames: [],
      selectedSheetName: null,

      // NOWE POLA DO OBŁSUGI POMIJANIA WIERSZY
      rowsToSkip: 1, // Domyślnie 1
      fullRawDataAsArrays: [], // Surowe dane jako tablica tablic (macierz)

      mapping: {}, // Klucz: fieldDefinition.key -> Wartość: fileHeaderName
      showIdentifierValidationError: false
    };
  },
  computed: {
    // 1. Oblicza surowe dane po odcięciu wierszy śmieciowych
    effectiveRawArrays() {
      if (!this.fullRawDataAsArrays || this.fullRawDataAsArrays.length === 0) return [];
      const skip = Math.max(0, this.rowsToSkip);
      // Zabezpieczenie, żeby nie pominąć więcej niż mamy
      if (skip >= this.fullRawDataAsArrays.length) return [];
      return this.fullRawDataAsArrays.slice(skip);
    },

    // 2. Wyznacza nagłówki z pierwszego wiersza "efektywnych" danych
    fileHeaders() {
      if (this.effectiveRawArrays.length === 0) return [];
      // Bierzemy pierwszy wiersz. Upewniamy się, że nagłówki to stringi.
      // Puste komórki w nagłówku zamieniamy na pusty string (obsłużone w template).
      return this.effectiveRawArrays[0].map(h => (h !== null && h !== undefined) ? String(h).trim() : "");
    },

    // 3. Konwertuje tablice na obiekty JSON używając wyznaczonych nagłówków
    processedJsonData() {
      // Potrzebujemy przynajmniej 2 wierszy: 1 na nagłówek + 1 na dane
      if (this.effectiveRawArrays.length < 2) return [];

      const headers = this.fileHeaders;
      // Dane zaczynają się od drugiego wiersza (indeks 1)
      const dataRowsAsArrays = this.effectiveRawArrays.slice(1);

      return dataRowsAsArrays.map(rowArray => {
        const rowObject = {};
        headers.forEach((header, index) => {
          // Jeśli nagłówek jest pusty, ignorujemy tę kolumnę
          if(!header) return;

          // Przypisujemy wartość z odpowiednim indeksem, lub pusty string jeśli brak
          rowObject[header] = (rowArray[index] !== undefined && rowArray[index] !== null) ? rowArray[index] : "";
        });
        return rowObject;
      });
    },

    // 4. Podgląd na bazie przetworzonych danych
    previewData() {
      return this.processedJsonData.slice(0, this.previewRows);
    },

    // Walidacja czy wszystkie wymagane pola są zmapowane
    isMappingValid() {
      if(this.fileHeaders.length === 0) return false;
      return this.fieldDefinitions
          .filter(f => f.required)
          .every(f => this.mapping[f.key] != null);
    },
    isIdentifierMappingValid() {
      if (!this.identifierKeys || this.identifierKeys.length === 0) return true;
      return this.identifierKeys.some(key => this.mapping[key]);
    },
    identifierFieldLabels() {
      if (!this.identifierKeys || this.identifierKeys.length === 0) return [];
      const labels = this.identifierKeys.map(key => {
        const match = this.fieldDefinitions.find(field => field.key === key);
        return match ? match.label : key;
      });
      return Array.from(new Set(labels));
    }
  },
  watch: {
    // OBSERWATOR: Gdy zmieniają się nagłówki (np. przez zmianę rowsToSkip),
    // spróbuj automatycznie dopasować mapowanie na nowo.
    fileHeaders: {
      handler(newHeaders) {
        if (newHeaders.length > 0) {
          this.autoMapHeaders();
        } else {
          this.mapping = {}; // Reset mapowania jeśli brak nagłówków
        }
      },
      deep: true,
      immediate: false
    },
    mapping: {
      handler() {
        if (this.showIdentifierValidationError && this.isIdentifierMappingValid) {
          this.showIdentifierValidationError = false;
        }
      },
      deep: true
    }
  },
  methods: {
    onFileChange(e) {
      const files = e.target.files;
      if (!files || files.length === 0) return;
      this.file = files[0];

      const ext = this.file.name.split('.').pop().toLowerCase();

      if (['xls', 'xlsx'].includes(ext)) {
        this.parseExcel();
      } else if (ext === 'csv') {
        this.parseCsv();
      } else {
        Swal.fire("Błąd", "Nieobsługiwany format pliku. Wybierz .xlsx, .xls lub .csv", "error");
        this.reset();
      }
    },

    parseExcel() {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          this.workbook = XLSX.read(data, { type: 'array' });
          this.sheetNames = this.workbook.SheetNames;

          if (this.sheetNames.length > 0) {
            this.selectedSheetName = this.sheetNames[0];
            this.step = 2;
            if(this.sheetNames.length === 1) {
              this.loadSheet();
            }
          } else {
            throw new Error("Plik Excel nie zawiera arkuszy.");
          }
        } catch (err) {
          Swal.fire("Błąd odczytu pliku", err.message, "error");
          this.reset();
        }
      };
      reader.readAsArrayBuffer(this.file);
    },

    loadSheet() {
      const worksheet = this.workbook.Sheets[this.selectedSheetName];
      // ZMIANA: header: 1 parsuje do tablicy tablic (macierzy)
      // defval: "" zapewnia, że puste komórki to puste stringi a nie undefined
      this.fullRawDataAsArrays = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: "" });

      if (!this.fullRawDataAsArrays || this.fullRawDataAsArrays.length === 0) {
        Swal.fire("Pusty arkusz", "Wybrany arkusz nie zawiera danych", "warning");
        return;
      }

      // Przechodzimy do kroku 3. Watcher na fileHeaders odpali autoMapHeaders.
      this.step = 3;
    },

    parseCsv() {
      Papa.parse(this.file, {
        header: false, // ZMIANA: Parsujemy jako tablice tablic, nie obiekty
        skipEmptyLines: false, // Nie pomijamy nic automatycznie, user decyduje
        complete: (results) => {
          if (results.errors.length > 0) {
            console.warn("CSV Errors (non-critical):", results.errors);
          }
          this.fullRawDataAsArrays = results.data;

          if (!this.fullRawDataAsArrays || this.fullRawDataAsArrays.length === 0) {
            Swal.fire("Pusty plik CSV", "Plik nie zawiera danych", "warning");
            this.reset();
            return;
          }
          this.step = 3;
        },
        error: (err) => {
          Swal.fire("Błąd parsowania CSV", err.message, "error");
          this.reset();
        }
      });
    },

    autoMapHeaders() {
      this.mapping = {};
      const currentHeaders = this.fileHeaders.map(h => h.toLowerCase().trim());

      this.fieldDefinitions.forEach(field => {
        const normalizedFieldLabel = field.label.toLowerCase().trim();
        const normalizedFieldKey = field.key.toLowerCase().trim();

        // Szukamy dopasowania po etykiecie lub kluczu
        const matchIndex = currentHeaders.findIndex(h =>
            h && (h === normalizedFieldLabel || h === normalizedFieldKey)
        );

        if (matchIndex !== -1) {
          // Przypisujemy oryginalną nazwę nagłówka z pliku
          this.mapping[field.key] = this.fileHeaders[matchIndex];
        } else {
          this.mapping[field.key] = null;
        }
      });
    },

    reset() {
      this.step = 1;
      this.file = null;
      this.workbook = null;
      this.sheetNames = [];
      this.selectedSheetName = null;
      this.rowsToSkip = 0; // Reset pomijania
      this.fullRawDataAsArrays = [];
      this.mapping = {};
      this.showIdentifierValidationError = false;
    },

    processImport() {
      if (!this.isIdentifierMappingValid) {
        this.showIdentifierValidationError = true;
        return;
      }
      // Używamy przetworzonych danych (processedJsonData)
      const sourceData = this.processedJsonData;

      if(sourceData.length === 0) {
        Swal.fire("Brak danych", "Brak rekordów do importu po zastosowaniu pomijania wierszy.", "warning");
        return;
      }

      const records = [];
      const errors = [];

      sourceData.forEach((row, index) => {
        const record = {};
        let rowValid = true;
        let rowErrors = [];

        for (const field of this.fieldDefinitions) {
          const sourceColName = this.mapping[field.key];

          // Jeśli pole nie jest zmapowane
          if (!sourceColName) {
            if (field.defaultValue !== undefined) {
              record[field.key] = field.defaultValue;
            } else if (field.required) {
              rowValid = false; // Powinno być zablokowane przez UI, ale dla bezpieczeństwa
            }
            continue;
          }

          // Pobierz wartość używając nazwy kolumny z pliku
          let val = row[sourceColName];

          // --- Konwersja typów ---
          if (field.type === 'number') {
            if(typeof val === 'string') {
              // Zamień przecinki na kropki, usuń spacje (np. "1 200,50" -> "1200.50")
              val = val.replace(/\s/g, '').replace(',', '.');
            }
            if(val === "" || val === null || val === undefined) {
              val = null;
            } else {
              const numVal = Number(val);
              if (isNaN(numVal)) {
                rowErrors.push(`Pole "${field.label}" ma nieprawidłowy format liczbowy: "${val}"`);
                val = null; // lub zostawiamy, zależy od backendu
                // Jeśli jest wymagane i nie jest liczbą, to błąd walidacji
                if(field.required) rowValid = false;
              } else {
                val = numVal;
              }
            }
          } else if (field.type === 'boolean') {
            if(typeof val === 'string') {
              const lowerVal = val.toLowerCase().trim();
              val = ['true', '1', 'tak', 'yes', 'prawda'].includes(lowerVal);
            } else {
              val = Boolean(val);
            }
          }
          // TODO: Data parsing (można rozbudować o moment.js/date-fns jeśli potrzebne)

          if ((val === null || val === undefined || val === '') && field.defaultValue !== undefined) {
            val = field.defaultValue;
          }

          // --- Walidacja wymagalności ---
          if (field.required && (val === null || val === undefined || val === '')) {
            rowErrors.push(`Brak wymaganego pola "${field.label}"`);
            rowValid = false;
          }

          record[field.key] = val;
        }

        if (rowValid && rowErrors.length === 0) {
          records.push(record);
        } else if (rowErrors.length > 0) {
          // Dodajemy błędy z numerem wiersza (uwzględniając pominięte + nagłówek)
          const actualRowNumber = this.rowsToSkip + 1 + (index + 1);
          errors.push(`Wiersz pliku ${actualRowNumber}: ${rowErrors.join(', ')}`);
        }
      });

      if (errors.length > 0) {
        let errorHtml = `<p>Znaleziono błędy w <b>${errors.length}</b> wierszach. Te wiersze zostaną pominięte.</p>`;
        errorHtml += `<div class="text-start bg-light p-2 border rounded" style="max-height: 200px; overflow-y: auto; font-size: 0.9em;">`;
        errorHtml += errors.slice(0, 20).join('<br>');
        if(errors.length > 20) errorHtml += `<br>...i ${errors.length - 20} więcej.`;
        errorHtml += `</div>`;

        if (records.length === 0) {
          Swal.fire({ icon: 'error', title: "Błędy walidacji", html: errorHtml + "<p class='mt-2 text-danger'>Brak poprawnych rekordów do importu.</p>" });
          return;
        }

        Swal.fire({
          title: 'Wykryto błędy walidacji',
          html: errorHtml + `<p class="mt-3 fw-bold">Czy chcesz kontynuować import tylko poprawnych rekordów (${records.length})?</p>`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: `Tak, importuj ${records.length} rekordów`,
          cancelButtonText: 'Anuluj i popraw',
          confirmButtonColor: '#d33' // Ostrzegawczy kolor
        }).then((result) => {
          if (result.isConfirmed) {
            this.finalize(records);
          }
        });

      } else {
        // Wszystko OK
        if(records.length === 0) {
          Swal.fire("Info", "Brak danych do zaimportowania.", "info");
          return;
        }

        Swal.fire({
          title: 'Potwierdzenie importu',
          text: `Gotowych do importu: ${records.length} rekordów. Czy kontynuować?`,
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: 'Tak, wyślij',
          cancelButtonText: 'Anuluj'
        }).then((result) => {
          if (result.isConfirmed) {
            this.finalize(records);
          }
        });
      }
    },

    finalize(records) {
      if (this.mode === 'emit') {
        this.$emit('onSave', records);
      } else {
        if (!this.importType) {
          console.error("ImportType is required for submit mode");
          Swal.fire("Błąd konfiguracji", "Brak typu importu.", "error");
          return;
        }

        this.commonImportModule.httpSubmitGenericImport({
          importType: this.importType,
          records: records,
          meta: this.meta
        }).then(response => {
          this.$emit('onSaved', response);
          this.reset();
        }).catch(err => {
          this.$emit('onError', err);
          // Błąd jest już obsłużony przez Swal w store, ale można tu dodać własną logikę
        });
      }
    }
  }
};
</script>

<style scoped>
.common-import-form {
  border: 1px solid #e0e0e0;
}
/* Ulepszony scrollbar dla tabel */
.table-responsive {
  scrollbar-width: thin;
  scrollbar-color: #c1c1c1 #f1f1f1;
}
.table-responsive::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
.table-responsive::-webkit-scrollbar-track {
  background: #f1f1f1;
}
.table-responsive::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}
.table-responsive::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Sticky header dla podglądu */
.sticky-top {
  position: sticky;
  top: 0;
  z-index: 10;
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
}
</style>
